"use client"

import Button from "components/Buttons/Button"
import Kicker from "components/Kicker"
import { useParallax } from "hooks/useParallax"
import EightVCIMG from "images/about/arloDefined/8VC.png"
import IlloBgIMG from "images/about/arloDefined/Background.webp"
import IlloFgIMG from "images/about/arloDefined/Foreground.webp"
import GCIMG from "images/about/arloDefined/GeneralCatalyst.png"
import IlloMobileIMG from "images/about/arloDefined/IllustrationMobile.png"
import IlloTabletIMG from "images/about/arloDefined/IllustrationTablet.png"
import UpfrontIMG from "images/about/arloDefined/Upfront.png"
import StaticImage from "library/StaticImage"
import useCanHover from "library/canHover"
import { css, f, styled } from "library/styled"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

const MOVEMENT_PERCENT = -2

export default function ArloDefined() {
	const canHover = useCanHover()
	const { parallaxRef } = useParallax({
		movementPercent: MOVEMENT_PERCENT,
		canHover,
		additionalYOffset: MOVEMENT_PERCENT,
	})

	return (
		<Wrapper>
			<Inner>
				<Content>
					<Top>
						<div>
							<StyledKicker size="small">Arlo: Everything we are</StyledKicker>
							<H4>Flipping the Script on Insurance</H4>
						</div>
						<Text>
							Traditional carriers profit when care is delayed or denied. Arlo
							does the opposite—leveraging data and AI to spot issues early,
							keep costs predictable, and give small companies the playbook
							Fortune-500 firms enjoy. Prevention drives the savings; healthier
							teams reap the rewards.
						</Text>
						<Button href={links.contact}>Get Started</Button>
					</Top>
					<div>
						<P>Backed by industry leaders:</P>
						<Logos>
							<UpfrontLogo
								src={UpfrontIMG}
								width={88}
								height={24}
								alt="Upfront Logo"
							/>
							<GeneralCatalystLogo
								src={GCIMG}
								width={166}
								height={25}
								alt="General Catalyst Logo"
							/>
							<EightVCLogo
								src={EightVCIMG}
								width={60}
								height={24}
								alt="8VC Logo"
							/>
						</Logos>
					</div>
				</Content>
				<Illustration>
					<IllustrationBg src={IlloBgIMG} width={688} height={650} alt="" />
					<IllustrationFg
						ref={parallaxRef}
						src={IlloFgIMG}
						width={1169}
						height={882}
						alt=""
					/>
					<TabletIllustration
						src={IlloTabletIMG}
						width={896}
						height={681}
						alt=""
					/>
					<MobileIllustration
						src={IlloMobileIMG}
						width={327}
						height={678}
						alt=""
					/>
				</Illustration>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		background: ${colors.offWhite};
		place-items: center;
		display: grid;
	`),
})

const Inner = styled("div", {
	...f.responsive(css`
		width: 1440px;
		padding: 76px 32px 140px 63px;
		display: flex;
		gap: 65px;
	`),
	...f.tablet(css`
		width: 1024px;
		padding: 140px 64px 76px;
		flex-direction: column;
		gap: 32px;
	`),
	...f.mobile(css`
		width: 100%;
		padding: 72px 24px 24px;
		flex-direction: column;
		gap: 60px;
	`),
})

const Content = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		width: 592px;
		height: 650px;
	`),
	...f.tablet(css`
		width: 896px;
		height: 731px;
		padding-bottom: 24px;
	`),
	...f.mobile(css`
		width: 324px;
		height: auto;
		justify-content: unset;
	`),
})

const Illustration = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green100};
	`),
	...f.responsive(css`
		width: 688px;
		height: 650px;
		border-radius: 20px 0 0;
		position: relative;
		overflow: clip;
	`),
	...f.tablet(css`
		width: 896px;
		height: 681px;
		border-radius: 16px 16px 0 0;
	`),
	...f.mobile(css`
		width: 327px;
		height: 678px;
		border-radius: 16px 16px 0 0;
	`),
})

const illustrationBase = css`
	display: block;
	position: absolute;
	top: 0;
	left: 0;
`

const IllustrationFg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 1169px;
		height: 882px;
		bottom: 0;
		top: unset;
		max-inline-size: unset !important;
		max-block-size: unset !important;
	`),
	...f.tablet(css`
		display: none;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const IllustrationBg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 688px;
		height: 650px;
	`),
	...f.tablet(css`
		display: none;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const TabletIllustration = styled(StaticImage, {
	...f.responsive(css`
		display: none;
	`),
	...f.tablet(css`
		${illustrationBase};
		width: 896px;
		height: 681px;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const MobileIllustration = styled(StaticImage, {
	...f.responsive(css`
		display: none;
	`),
	...f.mobile(css`
		${illustrationBase};
		width: 327px;
		height: 678px;
	`),
})

const Top = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 40px;
	`),
	...f.mobile(css`
		gap: 36px;
		margin-bottom: 60px;
	`),
})

const StyledKicker = styled(Kicker, {
	...f.responsive(css`
		margin-bottom: 24px;
	`),
	...f.tablet(css`
		margin-bottom: 36px;
	`),
})

const H4 = styled("p", {
	...f.responsive(css`
		${textStyles.h4};
		color: ${colors.green500};
	`),
	...f.tablet(css`
		${textStyles.h3};
		margin-bottom: 30px;
		width: 599px;
	`),
	...f.mobile(css`
		${textStyles.h6};
		width: 287px;
	`),
})

const Text = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green400};
		width: 507px;
	`),
	...f.tablet(css`
		${textStyles.bodyL};
		width: 578px;
	`),
	...f.mobile(css`
		${textStyles.bodyS};
		width: 313px;
	`),
})

const P = styled("p", {
	...f.responsive(css`
		${textStyles.titleS};
		color: ${colors.green300};
	`),
	...f.mobile(css`
		text-align: center;
	`),
})

const Logos = styled("div", {
	...f.responsive(css`
		display: flex;
		gap: 80px;
		margin-top: 28px;
		align-items: center;
	`),
	...f.mobile(css`
		flex-direction: column;
		gap: 18px;
		margin-top: 30px;
	`),
})

const UpfrontLogo = styled(StaticImage, {
	...f.responsive(css`
		width: 88px;
		height: 24px;
	`),
})

const GeneralCatalystLogo = styled(StaticImage, {
	...f.responsive(css`
		width: 166px;
		height: 25px;
	`),
})

const EightVCLogo = styled(StaticImage, {
	...f.responsive(css`
		width: 60px;
		height: 24px;
	`),
})
